import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/marketing/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/marketing/components/ui/dialog';
import { useAuthStore } from '@/marketing/stores/useAuthStore';
import { extractErrorMessage } from '@/marketing/lib/errorMessage';
import { campaignScoutingAPIClient } from '@/marketing/infrastructure/influencer/campaign/scouting/campaignScoutingAPIClient';
import { patchScoutingRow, refreshActiveScoutingPage, markRelatedScoutingDataStale } from './scoutingCache';
import type { ScoutingViewRow } from './scoutingView';
import { validScoutingProducts } from './scoutingDetails';

export function ScoutingProductDialog({
  campaignId,
  row,
  onClose,
}: {
  campaignId: string;
  row: ScoutingViewRow;
  onClose: () => void;
}) {
  const products = row.details?.products;
  const [selected, setSelected] = useState(() => [...(products?.selectedSkus ?? [])]);
  const [error, setError] = useState<string>();
  const lock = useRef(false);
  const cancel = useRef<HTMLButtonElement>(null);
  const client = useQueryClient();
  const mutation = useMutation({
    mutationFn: async () => {
      const actor = useAuthStore.getState().user?.email?.trim();
      if (!actor) {
        throw new Error('Sign in again before changing scouting products.');
      }
      if (!products?.editable || !validScoutingProducts(products, selected)) {
        throw new Error('Review the required product choices.');
      }
      return campaignScoutingAPIClient.setProducts(campaignId, row.influencerId, {
        selectedSkus: selected,
        expectedSelectedSkus: products.selectedSkus,
        transitionBy: actor,
      });
    },
    onSuccess: (response) => {
      patchScoutingRow(client, campaignId, row.influencerId, (current) => ({
        ...current,
        creator: { ...current.creator, selectedSkus: response.selectedSkus },
        ...(current.details
          ? {
              details: {
                ...current.details,
                products: { ...current.details.products, selectedSkus: response.selectedSkus },
              },
            }
          : {}),
      }));
      markRelatedScoutingDataStale(client);
      onClose();
    },
    onError: async (failure: unknown) => {
      setError(extractErrorMessage(failure, 'Could not save products.'));
      await refreshActiveScoutingPage(client, campaignId);
    },
    onSettled: () => {
      lock.current = false;
    },
  });
  const save = () => {
    if (lock.current) {
      return;
    }
    lock.current = true;
    setError(undefined);
    mutation.mutate();
  };
  const toggle = (sku: string) =>
    setSelected((current) => (current.includes(sku) ? current.filter((value) => value !== sku) : [...current, sku]));
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !lock.current) {
          onClose();
        }
      }}
    >
      <DialogContent
        className="ss-marketing"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          cancel.current?.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle>Edit scouting products</DialogTitle>
          <DialogDescription>
            Choose campaign products for {row.creator?.handle ? `@${row.creator.handle}` : row.influencerId}. Saving
            products does not change their scouting decision.
          </DialogDescription>
        </DialogHeader>
        {products && (
          <div className="tw-max-h-[45vh] tw-space-y-3 tw-overflow-auto">
            <p className="tw-text-xs tw-text-muted-foreground">
              {products.groups.length
                ? products.groups
                    .map((group) => `${group.name ?? group.groupId}: choose ${group.choiceCount}`)
                    .join(' · ')
                : `Choose ${products.choiceCount ?? 0} product(s)`}
            </p>
            {products.options.map((option) => (
              <label
                key={option.sku}
                className="tw-flex tw-items-center tw-gap-2 tw-rounded tw-border tw-p-2 tw-text-sm"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(option.sku)}
                  disabled={mutation.isPending || (option.disabled && !selected.includes(option.sku))}
                  onChange={() => toggle(option.sku)}
                />
                <span>
                  {option.label}{' '}
                  <span className="tw-text-xs tw-text-muted-foreground">
                    {option.sku}
                    {option.disabled ? ' · Unavailable' : ''}
                  </span>
                </span>
              </label>
            ))}
            {selected
              .filter((sku) => !products.options.some((option) => option.sku === sku))
              .map((sku) => (
                <label key={sku} className="tw-flex tw-gap-2 tw-text-sm">
                  <input type="checkbox" checked disabled={mutation.isPending} onChange={() => toggle(sku)} />
                  {sku} · No longer in campaign pool
                </label>
              ))}
          </div>
        )}
        {error && (
          <div role="alert" className="tw-text-sm tw-text-red-700">
            <p>{error}</p>
            <p>Your draft is preserved. If products changed, close and review the refreshed row before reopening.</p>
          </div>
        )}
        <DialogFooter>
          <Button
            ref={cancel}
            variant="outline"
            disabled={mutation.isPending}
            onClick={() => {
              if (!lock.current) {
                onClose();
              }
            }}
          >
            Cancel
          </Button>
          <Button
            disabled={mutation.isPending || !products?.editable || !validScoutingProducts(products, selected)}
            onClick={save}
          >
            {mutation.isPending ? 'Saving products…' : 'Save products'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
